import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Progress } from "@bowfast/ui/components/progress";
import { cn } from "@bowfast/ui/lib/utils";
import { motion } from "framer-motion";
import { AnchorIcon, FlagIcon, Navigation2Icon, PenLineIcon, RulerIcon, SailboatIcon } from "lucide-react";
import { useState } from "react";
import { fadeUp, stagger } from "~/lib/animations";
import { fmtDate } from "~/lib/fmt";
import { VESSEL_GRADIENTS, WAYPOINT_TYPE_BADGE } from "~/pages/expeditions/expedition-detail.mock";
import type { ExpeditionRoute, ExpeditionVesselAssignment } from "~/pages/expeditions/expeditions.types";

// Derive waypoint type from position in list.
// TODO(backend/waypoints): add `type` field (DEPARTURE/STOPOVER/ANCHORAGE/ARRIVAL) to
// RouteWaypoint in schema so this can be driven by real data instead of position inference.
type WaypointDisplayType = "DEPARTURE" | "STOPOVER" | "ARRIVAL";

function waypointDisplayType(index: number, total: number): WaypointDisplayType {
  if (index === 0) return "DEPARTURE";
  if (index === total - 1) return "ARRIVAL";
  return "STOPOVER";
}

// Per-vessel live tracking state.
// TODO(backend/tracking): replace VESSEL_TRACKING with VesselPings WebSocket subscription data.
// When wired, each vessel will receive live position/speed/heading from AIS.
const VESSEL_TRACKING: Record<string, { coveredNm: number; speed: string; heading: string; position: string }> = {};

function waypointNodeClass(type: WaypointDisplayType): string {
  if (type === "DEPARTURE") return "border-abyss-500 bg-abyss-50 text-abyss-600 dark:bg-abyss-900";
  if (type === "ARRIVAL") return "border-shallow-500 bg-shallow-50 text-shallow-700 dark:bg-shallow-900";
  return "border-border bg-muted text-muted-foreground";
}

function WaypointIcon({ type, index }: Readonly<{ type: WaypointDisplayType; index: number }>) {
  if (type === "DEPARTURE") return <FlagIcon className="size-3" />;
  if (type === "ARRIVAL") return <AnchorIcon className="size-3" />;
  return <span>{index + 1}</span>;
}

function fmtCoords(lat: number, lng: number): string {
  const latDir = lat >= 0 ? "N" : "S";
  const lngDir = lng >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
}

export function RouteTab({
  route,
  vessels,
  isActive,
  // TODO(backend/tracking): coveredNm will come from VesselPings subscription once wired.
  coveredNm = 0,
}: Readonly<{
  route: ExpeditionRoute | null | undefined;
  vessels: ExpeditionVesselAssignment[];
  isActive: boolean;
  coveredNm?: number;
}>) {
  const waypoints = route?.waypoints ?? [];
  const distanceNm = route?.totalDistanceNm ?? 0;
  const isMultiVessel = vessels.length > 1;
  const [activeVesselId, setActiveVesselId] = useState(vessels[0]?.id ?? "");
  const activeVessel = vessels.find((v) => v.id === activeVesselId) ?? vessels[0];
  const vesselTrack = VESSEL_TRACKING[activeVesselId] ?? {
    coveredNm,
    speed: "—",
    heading: "—",
    position: "—",
  };
  const pct = distanceNm > 0 ? Math.round((vesselTrack.coveredNm / distanceNm) * 100) : 0;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-5">
      {/* Route map card */}
      <motion.div variants={fadeUp}>
        <Card>
          {/* Map area */}
          <div className="relative -mt-4 h-60 overflow-hidden rounded-t-xl bg-linear-to-b from-[#0a1929] to-[#0d2137]">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
                backgroundSize: "2.5rem 2.5rem",
              }}
              aria-hidden
            />
            <svg
              className="absolute inset-0 size-full"
              viewBox="0 0 800 240"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden={true}
            >
              <defs>
                <radialGradient id="glow-dep" cx="90" cy="150" r="80" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="rgba(14,165,233,0.14)" />
                  <stop offset="100%" stopColor="transparent" />
                </radialGradient>
                <radialGradient id="glow-arr" cx="710" cy="130" r="80" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="rgba(20,184,166,0.14)" />
                  <stop offset="100%" stopColor="transparent" />
                </radialGradient>
              </defs>
              <circle cx="90" cy="150" r="90" fill="url(#glow-dep)" />
              <circle cx="710" cy="130" r="90" fill="url(#glow-arr)" />
              <circle cx="120" cy="120" r="190" fill="none" stroke="rgba(14,165,233,0.05)" strokeWidth="1" />
              <circle cx="680" cy="120" r="190" fill="none" stroke="rgba(14,165,233,0.05)" strokeWidth="1" />
              <path
                d="M 90,150 C 210,72 350,56 450,70 S 610,105 710,130"
                stroke="rgba(14,165,233,0.28)"
                strokeWidth="2"
                strokeDasharray="8 5"
                fill="none"
              />
              {isActive && (
                <path d="M 90,150 C 210,72 350,56 450,70" stroke="rgba(14,165,233,0.9)" strokeWidth="2.5" fill="none" />
              )}
              <circle cx="90" cy="150" r="6" fill="#0ea5e9" />
              <circle cx="90" cy="150" r="11" fill="none" stroke="rgba(14,165,233,0.22)" strokeWidth="1.5" />
              <text x="90" y="175" textAnchor="middle" fill="rgba(255,255,255,0.32)" fontSize="9" fontWeight="600">
                DEPARTURE
              </text>
              <circle cx="710" cy="130" r="6" fill="#14b8a6" />
              <circle cx="710" cy="130" r="11" fill="none" stroke="rgba(20,184,166,0.22)" strokeWidth="1.5" />
              <text x="710" y="155" textAnchor="middle" fill="rgba(20,184,166,0.42)" fontSize="9" fontWeight="600">
                ARRIVAL
              </text>
              {isActive && (
                <>
                  <circle cx="450" cy="70" r="5" fill="#22c55e" />
                  <circle cx="450" cy="70" r="10" fill="none" stroke="rgba(34,197,94,0.35)" strokeWidth="1.5">
                    <animate attributeName="r" values="10;15;10" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="1;0.2;1" dur="2s" repeatCount="indefinite" />
                  </circle>
                </>
              )}
            </svg>
            <span className="absolute bottom-3 right-4 text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-white/20">
              Route preview
            </span>
          </div>

          <CardContent className="space-y-3 pt-4">
            {isMultiVessel && (
              <div className="flex flex-wrap gap-2">
                {vessels.map((v, vi) => {
                  const isSelected = v.id === activeVesselId;
                  const grad = VESSEL_GRADIENTS[vi % VESSEL_GRADIENTS.length] ?? VESSEL_GRADIENTS[0];
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setActiveVesselId(v.id)}
                      className={cn(
                        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
                        isSelected
                          ? "border-abyss-500 bg-abyss-50 font-semibold text-abyss-700 dark:bg-abyss-950 dark:text-abyss-300"
                          : "border-border bg-card text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <div
                        className={cn(
                          "flex size-6 shrink-0 items-center justify-center rounded-md bg-linear-to-br",
                          grad,
                        )}
                      >
                        <SailboatIcon className="size-3.5 text-white" />
                      </div>
                      {/* TODO(backend/vessels): add `flag` emoji field to Vessel type */}
                      <span>{v.vessel.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {isActive && (
              <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg bg-muted/40 px-4 py-3">
                <div>
                  <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">Vessel</p>
                  {/* TODO(backend/vessels): add `flag` emoji field to Vessel type */}
                  <p className="font-heading text-sm font-semibold">{activeVessel?.vessel.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    Position
                  </p>
                  {/* TODO(backend/tracking): wire from VesselPings subscription */}
                  <p className="font-heading text-sm font-semibold tabular-nums">{vesselTrack.position}</p>
                </div>
                <div>
                  <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">Speed</p>
                  <p className="font-heading text-sm font-semibold tabular-nums">{vesselTrack.speed}</p>
                </div>
                <div>
                  <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">Heading</p>
                  <p className="font-heading text-sm font-semibold tabular-nums">{vesselTrack.heading}</p>
                </div>
                <div>
                  <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    Progress
                  </p>
                  <p className="font-heading text-sm font-semibold tabular-nums">
                    {vesselTrack.coveredNm.toLocaleString()} / {distanceNm.toLocaleString()} nm
                  </p>
                </div>
              </div>
            )}
          </CardContent>

          {isActive && (
            <CardFooter className="gap-3 px-5 py-3">
              <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
                {vesselTrack.coveredNm.toLocaleString()} nm
              </span>
              <Progress value={pct} className="h-2 flex-1" />
              <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">{pct}%</span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {distanceNm.toLocaleString()} nm
              </span>
            </CardFooter>
          )}
        </Card>
      </motion.div>

      {/* Waypoint table */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <Navigation2Icon className="size-4 text-abyss-500" />
                Waypoints
                <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[0.625rem] font-bold text-muted-foreground">
                  {waypoints.length}
                </span>
              </CardTitle>
              <Button size="sm" variant="outline" className="gap-1.5">
                <PenLineIcon className="size-3.5" />
                Edit route
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {waypoints.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">No waypoints on this route yet.</p>
            ) : (
              waypoints.map((wp, i) => {
                const type = waypointDisplayType(i, waypoints.length);
                const etaLabel = wp.plannedArrival ? fmtDate(wp.plannedArrival) : undefined;
                return (
                  <div key={wp.id}>
                    <div className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-muted/30">
                      <div
                        className={cn(
                          "flex size-7 shrink-0 items-center justify-center rounded-full border-2 text-[0.625rem] font-bold",
                          waypointNodeClass(type),
                        )}
                      >
                        <WaypointIcon type={type} index={i} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-heading text-sm font-semibold leading-tight">
                          {wp.name ?? `Waypoint ${i + 1}`}
                        </p>
                        <p className="text-xs tabular-nums text-muted-foreground">{fmtCoords(wp.lat, wp.lng)}</p>
                      </div>
                      <span
                        className={cn(
                          "hidden shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-bold sm:inline-block",
                          WAYPOINT_TYPE_BADGE[type].className,
                        )}
                      >
                        {WAYPOINT_TYPE_BADGE[type].label}
                      </span>
                      <div className="w-16 text-right">
                        {etaLabel ? (
                          <p className="text-sm tabular-nums font-medium">{etaLabel}</p>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>
                    {i < waypoints.length - 1 && (
                      <div className="ml-[2.15rem] flex items-center gap-2.5 py-1.5" aria-hidden>
                        <div className="h-6 w-px bg-border" />
                        {/* TODO(backend/waypoints): add per-leg distance (estimatedLegNm) to RouteWaypoint */}
                        {wp.estimatedLegHours != null && (
                          <span className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                            <RulerIcon className="size-3" />~{wp.estimatedLegHours}h
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
